<template>
	<section
		v-if="gastroProducts?.result?.length && visitedHypermarketInstance"
		:class="isGrayBackground && 'bg-background-secondary py-4 sm:py-10'"
	>
		<div class="container">
			<div class="flex flex-col gap-y-6 mb-6 md:mb-8">
				<h2 v-if="blok.title" class="typo-h1 text-heading-primary mb-0" v-text="blok.title" />
				<RichText
					v-if="blok.text"
					:data="blok.text"
					class="typo-body-l text-body-quaternary"
				/>
			</div>
			<div class="overflow-x-hidden">
				<CarouselSkeleton
					:items="gastroProducts?.result ?? []"
					:has-first-item="!!(blok.promotionTileDesktopLarge?.filename || blok.promotionTileDesktop?.filename || blok.promotionTileMobile?.filename)"
					class="mb-4 md:mb-6"
					list-class="inline-flex w-full min-w-max gap-6 xl:min-w-full xl:w-fit xl:grid xl:grid-cols-5"
					page-per-item
					snap-items
					center-item-snap
				>
					<template #firstItem>
						<li
							v-if="blok.promotionTileDesktopLarge?.filename || blok.promotionTileDesktop?.filename || blok.promotionTileMobile?.filename"
							class="relative"
						>
							<NuxtPicture
								v-if="blok.promotionTileDesktop?.filename"
								provider="storyblok"
								height="394"
								width="311"
								:modifiers="storyblokFocusModifiers(blok.promotionTileDesktop.focus)"
								:src="blok.promotionTileDesktop.filename"
								:alt="blok.promotionTileDesktop.alt || ''"
								sizes="311px"
								:img-attrs="{class:'rounded-2.5xl w-full'}"
								loading="lazy"
								class="hidden sm:block"
							/>
							<NuxtPicture
								v-if="blok.promotionTileMobile?.filename"
								provider="storyblok"
								height="280"
								width="228"
								:modifiers="storyblokFocusModifiers(blok.promotionTileMobile.focus)"
								:src="blok.promotionTileMobile.filename"
								:alt="blok.promotionTileMobile.alt || ''"
								sizes="228px"
								:img-attrs="{class:'rounded-2.5xl w-full'}"
								loading="lazy"
								class="sm:hidden"
							/>
						</li>
					</template>
					<template #item="{item: product}">
						<li :key="product.offerId" class="rem:w-[230px] xl:w-full">
							<AbsoluteInternalLink :to="branchSpecificPath({name: 'online-objednavka-slug', params: {slug: 'restaurace'}})">
								<GOOSProductTile :product="product" is-static class="h-full bg-white" />
							</AbsoluteInternalLink>
						</li>
					</template>
				</CarouselSkeleton>
			</div>
			<div class="w-full flex justify-center">
				<AbsoluteInternalLink
					class="btn btn-primary btn-lg max-sm:justify-center mx-auto"
					:to="branchSpecificPath({name: 'online-objednavka-slug', params: {slug: 'restaurace'}})"
				>
					Objednat online
					<NuxtIcon
						name="arrow-right-circle"
						class="text-2xl"
						filled
					/>
				</AbsoluteInternalLink>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {RESTAURANT_MEAL_PRESENTATION_FRONTEND_ID} from '#shared/constants';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useGoosGastroStore} from '~/stores/goos/gastro';
	import {ProductionTypeIdEnum} from '~/stores/shoppingCart';
	import type {RestaurantMealPresentationStoryblok} from '~/storyblok/types';
	import useBranchPageAvailability from '~/composables/useBranchPageAvailability';
	import {productIsPreferredMenu} from '~/utils/productIsPreferredMenu';

	interface Props {
		blok: RestaurantMealPresentationStoryblok;
	}

	defineProps<Props>();

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);
	const {visitedHypermarketInstance} = useVisitedHypermarket();
	const branchSpecificPath = useBranchSpecificPath();
	const visitedBranchSlug = computed(() => visitedHypermarketInstance.value?.slug ?? null);
	const {data: branchPageAvailability, loading: branchPageAvailabilityLoading} = useBranchPageAvailability(visitedBranchSlug);

	const goosGastroStore = useGoosGastroStore();

	const route = useRoute();
	const isGrayBackground = computed(() => route.name === 'branch-sluzby-restaurace');

	const {data: gastroProducts} = await useAsyncData(
		`RestaurantMealPresentation-${visitedHypermarketInstance.value?.content?.gsoaId || 'globus'}`,
		async () => {
			if (!visitedHypermarketInstance.value || branchPageAvailabilityLoading.value || !branchPageAvailability.value.hasRestaurant) {
				return null;
			}

			const data = await goosGastroStore.fetchOffers({
				localityId: Number(visitedHypermarketInstance.value.content.gsoaId),
				productTypeId: ProductionTypeIdEnum.RESTAURANT,
				offset: 0,
				limit: 5,
				frontendId: RESTAURANT_MEAL_PRESENTATION_FRONTEND_ID,
				onlyOrderableNow: false,
				onlyOrderableToday: true,
			}).catch(() => null);

			if (!data?.result) {
				return data;
			}

			return {
				...data,
				result: [...data.result].sort((a, b) => Number(productIsPreferredMenu(b)) - Number(productIsPreferredMenu(a))),
			};
		},
		{watch: [selectedHypermarket, branchPageAvailability, branchPageAvailabilityLoading]},
	);
</script>
